// src/views/HomeView.js — extracted from App.js
import React, { useState, useEffect, useCallback } from "react";
import { SellerCard } from "../components/common";
import { supabase } from "../lib/supabase";
import { useLanguage, t, getHeroLines } from "../lib/i18n";

// 21 curated vintage HMT poster ads — rotated as hero backdrop on each line switch
const POSTER_POOL = [
  "/bg-images/p2_18.jpg", // take your time — young man, cinematic
  "/bg-images/p1_1.jpg",  // couple "you are in time"
  "/bg-images/p2_15.jpg", // woman with accordion
  "/bg-images/p2_1.jpg",  // boy + father's watch
  "/bg-images/p1_6.jpg",  // Indian woman in garden
  "/bg-images/p1_10.jpg", // woman with bindi "give her the time of her life"
  "/bg-images/p2_14.jpg", // group of young Indians — 30 million HMT owners
  "/bg-images/p1_15.jpg", // couple at table "every moment is precious"
  "/bg-images/p2_2.jpg",  // child holding watch triumphantly
  "/bg-images/p2_10.jpg", // Avinash billiards, dark moody
  "/bg-images/p2_17.jpg", // Ajeeth rowboat
  "/bg-images/p1_3.jpg",  // Everest expedition
  "/bg-images/p1_7.jpg",  // HMT Chinar above city skyline
  "/bg-images/p1_11.jpg", // Vijay tennis player
  "/bg-images/p1_2.jpg",  // "Swim with it" — watch + tropical fish
  "/bg-images/p1_4.jpg",  // watches spread on mossy rocks (colour)
  "/bg-images/p1_12.jpg", // After Hours — watches on dark city aerial
  "/bg-images/p1_16.jpg", // "You'd give your right arm" — 5 watches on rocks
  "/bg-images/p2_7.jpg",  // watches on dark background, city lights
  "/bg-images/p1_5.jpg",  // watches spread on rocks
  "/bg-images/p1_14.jpg", // festive gift — watches + flowers
];

// ─── HOME VIEW ────────────────────────────────────────────────────────────────
export function HomeView({ setView, sellers, session, openAuth, onSellClick, catalogEntries = [] }) {
  const { lang } = useLanguage();
  const HERO_LINES = getHeroLines(lang);

  const [heroIdx, setHeroIdx]         = useState(0);
  const [prevIdx, setPrevIdx]         = useState(null);
  const [clipsVisible, setClipsVisible] = useState(true);
  const [posterOffset, setPosterOffset] = useState(0);
  const [completedCount, setCompletedCount] = useState(null);
  const [catalogCount, setCatalogCount]     = useState(null);

  useEffect(() => {
    const iv = setInterval(() => {
      setClipsVisible(false);
      setHeroIdx(current => { setPrevIdx(current); return (current + 1) % HERO_LINES.length; });
      setPosterOffset(p => p + 1);
      setTimeout(() => { setClipsVisible(true); setPrevIdx(null); }, 550);
    }, 5000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line

  useEffect(() => {
    supabase.from("requests").select("id", { count: "exact", head: true })
      .eq("status", "Completed")
      .then(({ count }) => { if (count != null) setCompletedCount(count); });
    supabase.from("catalogEntries").select("id", { count: "exact", head: true })
      .eq("status", "approved")
      .then(({ count }) => { if (count != null) setCatalogCount(count); });
  }, []);

  const { top, accent } = HERO_LINES[heroIdx];
  const verifiedSellers = sellers.filter(s => s.verified);

  // Pick catalog entries with photos for preview
  const previewEntries = catalogEntries
    .filter(e => e.status === "approved" && e.photos?.length > 0)
    .slice(0, 4);

  // Clip slots — position/size/rotation/mask only; src comes from POSTER_POOL dynamically
  // br = brightness, sat = saturation (right-side clips are brighter/more saturated so they're visible)
  const CLIPPINGS = [
    { w:460, right:"70%", top:"0%",  rot:-7,  depth:0.08, br:0.32, sat:0.25, dur:22,
      mask:"radial-gradient(ellipse 72% 68% at 55% 46%, black 30%, rgba(0,0,0,.25) 58%, transparent 80%)" },
    { w:420, right:"55%", top:"15%", rot:13,  depth:0.14, br:0.36, sat:0.28, dur:16,
      mask:"radial-gradient(ellipse 76% 72% at 50% 42%, black 34%, rgba(0,0,0,.28) 60%, transparent 82%)" },
    { w:370, right:"44%", top:"5%",  rot:19,  depth:0.20, br:0.40, sat:0.32, dur:10,
      mask:"radial-gradient(ellipse 78% 73% at 48% 40%, black 36%, rgba(0,0,0,.28) 62%, transparent 83%)" },
    { w:320, right:"37%", top:"32%", rot:-17, depth:0.18, br:0.38, sat:0.30, dur:8,
      mask:"radial-gradient(ellipse 74% 70% at 52% 42%, black 32%, rgba(0,0,0,.24) 60%, transparent 81%)" },
    { w:410, right:"22%", top:"2%",  rot:-14, depth:0.60, br:0.72, sat:0.55, dur:12,
      mask:"radial-gradient(ellipse 88% 84% at 50% 44%, black 44%, rgba(0,0,0,.28) 66%, transparent 88%)" },
    { w:370, right:"19%", top:"40%", rot:-6,  depth:0.54, br:0.68, sat:0.50, dur:15,
      mask:"radial-gradient(ellipse 86% 82% at 50% 48%, black 42%, rgba(0,0,0,.26) 64%, transparent 86%)" },
    { w:420, right:"2%",  top:"22%", rot:8,   depth:0.70, br:0.78, sat:0.60, dur:17,
      mask:"radial-gradient(ellipse 90% 86% at 50% 44%, black 46%, rgba(0,0,0,.30) 68%, transparent 90%)" },
    { w:460, right:"-3%", top:"0%",  rot:-4,  depth:0.88, br:0.85, sat:0.65, dur:13,
      mask:"radial-gradient(ellipse 92% 88% at 50% 40%, black 48%, rgba(0,0,0,.32) 70%, transparent 90%)" },
  ];

  // Pick 8 posters for this frame — rotate through pool on every hero switch
  const clipSrcs = Array.from({length: CLIPPINGS.length}, (_, i) =>
    POSTER_POOL[(posterOffset * 3 + i) % POSTER_POOL.length]
  );

  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const handleMouse = useCallback((e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setMouse({ x: (e.clientX - r.left) / r.width - 0.5, y: (e.clientY - r.top) / r.height - 0.5 });
  }, []);

  const stats = [
    ...(completedCount > 0 ? [{ n: completedCount, label: "watches sourced" }] : []),
    ...(catalogCount > 0  ? [{ n: catalogCount,    label: "catalog entries" }] : []),
  ];

  return (
    <div className="fade-in">

      {/* Float keyframes + hero cross-dissolve + mobile clipping visibility */}
      <style>{`
        @keyframes heroIn{from{opacity:0;filter:blur(16px);transform:translateY(8px)}to{opacity:1;filter:blur(0);transform:translateY(0)}}
        @keyframes heroOut{from{opacity:1;filter:blur(0);transform:translateY(0)}to{opacity:0;filter:blur(16px);transform:translateY(-8px)}}
        @keyframes hmtF0{0%,100%{transform:translate(0,0)}40%{transform:translate(6px,-11px)}75%{transform:translate(-5px,7px)}}
        @keyframes hmtF1{0%,100%{transform:translate(0,0)}52%{transform:translate(-8px,-6px)}}
        @keyframes hmtF2{0%,100%{transform:translate(0,0)}36%{transform:translate(4px,9px)}70%{transform:translate(-6px,-4px)}}
        @keyframes hmtF3{0%,100%{transform:translate(0,0)}44%{transform:translate(-7px,8px)}80%{transform:translate(5px,-6px)}}
        @keyframes hmtF4{0%,100%{transform:translate(0,0)}55%{transform:translate(7px,5px)}}
        @keyframes hmtF5{0%,100%{transform:translate(0,0)}32%{transform:translate(-5px,-9px)}62%{transform:translate(6px,4px)}}
        @keyframes hmtF6{0%,100%{transform:translate(0,0)}48%{transform:translate(8px,-7px)}}
        @keyframes hmtF7{0%,100%{transform:translate(0,0)}42%{transform:translate(-6px,10px)}77%{transform:translate(4px,-5px)}}
        @media(max-width:767px){.hmt-clip-desk{display:none!important;}.hmt-clip-mob{display:block!important;}.hero-bot-fade{height:140px!important;}.thc-home-hero{margin-top:-20px!important;}}
        @media(min-width:768px){.hmt-clip-mob{display:none!important;}}
      `}</style>

      {/* ── Hero — full viewport width, breaks out of max-width container ── */}
      {/* zIndex:0 creates stacking context that keeps hero below sticky header (z-index:100) */}
      <div className="thc-home-hero" style={{
        position:"relative", zIndex:0, overflow:"hidden",
        background:"#06090F",
        marginTop:-52,marginLeft:"calc(-50vw + 50%)",marginRight:"calc(-50vw + 50%)",
        width:"100vw",height:600,
        display:"flex",alignItems:"stretch",marginBottom:0,
      }} onMouseMove={handleMouse} onMouseLeave={()=>setMouse({x:0,y:0})}>

        {/* Desktop: atmospheric shapes, fade in/out on each hero switch */}
        <div className="hmt-clip-desk" style={{
          position:"absolute", inset:0, pointerEvents:"none",
          opacity: clipsVisible ? 1 : 0,
          filter: clipsVisible ? "blur(0px)" : "blur(10px)",
          transition: "opacity 0.44s cubic-bezier(.4,0,.2,1), filter 0.44s cubic-bezier(.4,0,.2,1)",
        }}>
          {CLIPPINGS.map((c,i) => (
            <div key={i} style={{
              position:"absolute", width:c.w, right:c.right, top:c.top,
              animation:`hmtF${i%8} ${c.dur}s ease-in-out infinite`,
            }}>
              <div style={{
                transform:`translate(${-mouse.x*c.depth*38}px,${-mouse.y*c.depth*22}px) rotate(${c.rot}deg)`,
                transition:"transform 1.6s cubic-bezier(0.25,0.46,0.45,0.94)",
                willChange:"transform",
              }}>
                <img src={clipSrcs[i]} alt="" style={{
                  width:"100%", display:"block",
                  filter:`brightness(${c.br}) saturate(${c.sat}) sepia(0.18)`,
                  maskImage:c.mask, WebkitMaskImage:c.mask,
                }}/>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: 2 switching posters, same fade as desktop */}
        <div className="hmt-clip-mob" style={{
          position:"absolute", inset:0, pointerEvents:"none", display:"none",
          opacity: clipsVisible ? 1 : 0,
          filter: clipsVisible ? "blur(0px)" : "blur(10px)",
          transition: "opacity 0.44s cubic-bezier(.4,0,.2,1), filter 0.44s cubic-bezier(.4,0,.2,1)",
        }}>
          <img src={clipSrcs[7]} alt="" style={{
            position:"absolute", right:"-8%", top:"-5%", width:"72%",
            filter:"brightness(0.52) saturate(0.25) sepia(0.22)",
            transform:"rotate(-5deg)",
            maskImage:"radial-gradient(ellipse 80% 82% at 40% 40%, black 35%, rgba(0,0,0,.3) 60%, transparent 82%)",
            WebkitMaskImage:"radial-gradient(ellipse 80% 82% at 40% 40%, black 35%, rgba(0,0,0,.3) 60%, transparent 82%)",
          }}/>
          <img src={clipSrcs[4]} alt="" style={{
            position:"absolute", right:"-14%", bottom:"-8%", width:"58%",
            filter:"brightness(0.38) saturate(0.22) sepia(0.25)",
            transform:"rotate(9deg)",
            maskImage:"radial-gradient(ellipse 76% 78% at 44% 44%, black 30%, rgba(0,0,0,.24) 56%, transparent 78%)",
            WebkitMaskImage:"radial-gradient(ellipse 76% 78% at 44% 44%, black 30%, rgba(0,0,0,.24) 56%, transparent 78%)",
          }}/>
        </div>

        {/* Unified dark overlay — lightened so posters breathe through */}
        <div style={{position:"absolute",inset:0,background:"linear-gradient(135deg,rgba(6,9,15,.38) 0%,rgba(6,9,15,.08) 52%,rgba(6,9,15,.28) 100%)",pointerEvents:"none"}}/>
        {/* Subtle green glow on right */}
        <div style={{position:"absolute",inset:0,background:"radial-gradient(ellipse 50% 60% at 74% 46%,rgba(5,100,65,.08) 0%,transparent 65%)",pointerEvents:"none"}}/>
        {/* Left text-protection vignette */}
        <div style={{position:"absolute",inset:0,background:"linear-gradient(to right,rgba(6,9,15,.98) 0%,rgba(6,9,15,.92) 10%,rgba(6,9,15,.75) 24%,rgba(6,9,15,.42) 38%,rgba(6,9,15,.12) 52%,transparent 62%)",pointerEvents:"none"}}/>
        {/* Right edge fade */}
        <div className="mob-hide" style={{position:"absolute",inset:0,background:"linear-gradient(to left,rgba(6,9,15,.3) 0%,transparent 14%)",pointerEvents:"none"}}/>

        {/* Fine grain overlay */}
        <div style={{position:"absolute",inset:0,opacity:.025,backgroundImage:"url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",backgroundSize:"180px",pointerEvents:"none"}}/>

        {/* Content — always above backdrop (zIndex:10 > any clipping) */}
        <div style={{position:"relative",zIndex:10,maxWidth:1060,margin:"0 auto",width:"100%",padding:"100px 24px 80px",display:"flex",alignItems:"center"}}>
          <div style={{maxWidth:640}}>
            {/* Rotating headline — cross-dissolve: outgoing fades out while incoming fades in */}
            <div style={{position:"relative",marginBottom:32}}>
              {prevIdx !== null && (
                <h1 key={`prev-${prevIdx}`} className="mob-hero-h1" style={{
                  position:"absolute",top:0,
                  fontFamily:"'Playfair Display',serif",fontSize:88,lineHeight:.97,
                  letterSpacing:"-0.04em",color:"#fff",
                  textShadow:"0 2px 24px rgba(0,0,0,.9)",
                  animation:"heroOut 0.55s forwards",
                  fontWeight:700,pointerEvents:"none",
                }}>
                  {HERO_LINES[prevIdx].top}<br/><em style={{color:"#6EE7B7",fontStyle:"italic",fontWeight:400}}>{HERO_LINES[prevIdx].accent}</em>
                </h1>
              )}
              <h1 key={`curr-${heroIdx}`} className="mob-hero-h1" style={{
                fontFamily:"'Playfair Display',serif",fontSize:88,lineHeight:.97,
                letterSpacing:"-0.04em",color:"#fff",
                textShadow:"0 2px 24px rgba(0,0,0,.9)",
                animation:prevIdx!==null?"heroIn 0.55s forwards":"none",
                opacity:prevIdx!==null?undefined:1,
                fontWeight:700,
              }}>
                {top}<br/><em style={{color:"#6EE7B7",fontStyle:"italic",fontWeight:400}}>{accent}</em>
              </h1>
            </div>

            <p className="mob-hero-sub" style={{fontSize:16,color:"rgba(255,255,255,.32)",lineHeight:1.9,maxWidth:420,marginBottom:40,letterSpacing:"0.005em"}}>
              {t("hero_sub", lang)}
            </p>

            <div className="mob-hero-btns" style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              <button onClick={()=>setView("catalog")} style={{background:"#fff",color:"#0C0C14",border:"none",padding:"15px 32px",fontSize:14,fontWeight:700,borderRadius:50,cursor:"pointer",letterSpacing:"-0.02em",transition:"transform .18s,box-shadow .18s"}}
                onMouseOver={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 10px 32px rgba(255,255,255,.18)";}}
                onMouseOut={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="";}}>
                {t("hero_browse", lang)}
              </button>
              <button onClick={()=>setView("request")} style={{background:"rgba(5,150,105,.1)",color:"#6EE7B7",border:"1.5px solid rgba(110,231,183,.22)",padding:"15px 28px",fontSize:14,borderRadius:50,cursor:"pointer",fontWeight:500,transition:"transform .18s,background .18s"}}
                onMouseOver={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.background="rgba(5,150,105,.22)";}}
                onMouseOut={e=>{e.currentTarget.style.transform="";e.currentTarget.style.background="rgba(5,150,105,.1)";}}>
                {t("hero_find", lang)}
              </button>
              {!session && <button onClick={openAuth} style={{background:"transparent",color:"rgba(255,255,255,.3)",border:"1.5px solid rgba(255,255,255,.1)",padding:"15px 22px",fontSize:13,borderRadius:50,cursor:"pointer"}}>{t("hero_signin", lang)}</button>}
            </div>

            {/* Trust signals — below CTAs */}
            <div style={{display:"flex",gap:0,marginTop:20,flexWrap:"wrap",alignItems:"center"}}>
              {[t("hero_tag1",lang), t("hero_tag2",lang), t("hero_tag3",lang)].map((tag,i,a)=>(
                <React.Fragment key={tag}>
                  <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.14em",color:"rgba(255,255,255,.28)",textTransform:"uppercase"}}>{tag}</span>
                  {i<a.length-1 && <span style={{color:"rgba(255,255,255,.12)",margin:"0 10px",fontSize:9}}>·</span>}
                </React.Fragment>
              ))}
            </div>

            {/* Stats inside hero */}
            {stats.length > 0 && (
              <div style={{display:"flex",gap:32,marginTop:40,flexWrap:"wrap"}}>
                {stats.map(s => (
                  <div key={s.label}>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:700,color:"#fff",letterSpacing:"-0.03em",lineHeight:1}}>{s.n.toLocaleString("en-IN")}</div>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"rgba(255,255,255,.35)",letterSpacing:"0.18em",textTransform:"uppercase",marginTop:4}}>{s.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Seamless bottom fade into page background — tall + gradual */}
        <div className="hero-bot-fade" style={{position:"absolute",bottom:0,left:0,right:0,height:320,background:"linear-gradient(to top,#F9F7F4 0%,rgba(249,247,244,.85) 18%,rgba(249,247,244,.4) 50%,rgba(249,247,244,.1) 75%,transparent 100%)",pointerEvents:"none"}}/>
      </div>

      {/* ── Catalog preview ──────────────────────────────────────────────── */}
      {previewEntries.length > 0 && (
        <div style={{marginTop:48,marginBottom:52}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:20}}>
            <div>
              <span className="sl">{t("sec_catalog",lang)}</span>
              <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:28,fontWeight:700,margin:0}}>{t("sec_catalog_h",lang)}</h2>
            </div>
            <button className="btn-ghost" style={{fontSize:12,padding:"8px 18px",flexShrink:0}} onClick={()=>setView("catalog")}>{t("browse_all",lang)}</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:14}}>
            {previewEntries.map(e => (
              <div key={e.id} onClick={()=>setView("catalog")} style={{borderRadius:16,overflow:"hidden",background:"#fff",border:"1px solid rgba(0,0,0,.05)",boxShadow:"0 2px 12px rgba(0,0,0,.06)",cursor:"pointer",transition:"transform .18s,box-shadow .18s"}}
                onMouseOver={el=>{el.currentTarget.style.transform="translateY(-3px)";el.currentTarget.style.boxShadow="0 12px 40px rgba(0,0,0,.13)";}}
                onMouseOut={el=>{el.currentTarget.style.transform="";el.currentTarget.style.boxShadow="0 2px 12px rgba(0,0,0,.06)";}}>
                <div style={{height:180,background:"#F2EFE9",overflow:"hidden"}}>
                  <img src={e.photos[0]} alt={e.model} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                </div>
                <div style={{padding:"12px 14px 14px"}}>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"var(--ink3)",letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:3}}>{e.brand}</div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,lineHeight:1.2,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{e.model}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── What we do ──────────────────────────────────────────────────── */}
      <div style={{marginTop: previewEntries.length > 0 ? 0 : 48, marginBottom:52}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:20}}>
          <div>
            <span className="sl">{t("sec_services",lang)}</span>
            <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:28,fontWeight:700,margin:0}}>{t("sec_services_h",lang)}</h2>
          </div>
        </div>
        <div className="home-services-grid" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:1,background:"var(--bd)",borderRadius:20,overflow:"hidden"}}>
          {[
            { lk:"svc_find",    sublk:"svc_find_sub",    plk:"svc_find_p",    v:"request",  bg:"#0F2318", accent:"#6EE7B7" },
            { lk:"svc_auth",    sublk:"svc_auth_sub",    plk:"svc_auth_p",    v:"auth-svc", bg:"#140F22", accent:"#C4B5FD" },
            { lk:"svc_market",  sublk:"svc_market_sub",  plk:"svc_market_p",  v:"market",   bg:"#1A140A", accent:"#FCD34D" },
            { lk:"svc_service", sublk:"svc_service_sub", plk:"svc_service_p", v:"services", bg:"#0A1A1A", accent:"#67E8F9" },
          ].map(s => (
            <div key={s.lk} onClick={()=>setView(s.v)}
              style={{background:s.bg,padding:"34px 26px 32px",cursor:"pointer",transition:"filter .2s",display:"flex",flexDirection:"column",justifyContent:"space-between",minHeight:180}}
              onMouseOver={e=>e.currentTarget.style.filter="brightness(1.18)"}
              onMouseOut={e=>e.currentTarget.style.filter=""}>
              <div>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:s.accent,letterSpacing:"0.2em",textTransform:"uppercase",marginBottom:16,opacity:.85}}>{t(s.plk,lang)}</div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,color:"#fff",lineHeight:1.15,marginBottom:10,fontWeight:700}}>{t(s.lk,lang)}</div>
                <div style={{fontSize:13,color:"rgba(255,255,255,.32)",lineHeight:1.65}}>{t(s.sublk,lang)}</div>
              </div>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:s.accent,letterSpacing:"0.12em",marginTop:20,opacity:.6}}>→</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── How it works ────────────────────────────────────────────────── */}
      <div style={{marginBottom:52,padding:"36px 0",borderTop:"1px solid var(--bd)"}}>
        <span className="sl">{t("sec_process",lang)}</span>
        <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:28,fontWeight:700,marginBottom:32}}>{t("sec_process_h",lang)}</h2>
        <div className="home-steps-grid" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:32}}>
          {[
            { n:"01", hk:"step1_h", bk:"step1_b" },
            { n:"02", hk:"step2_h", bk:"step2_b" },
            { n:"03", hk:"step3_h", bk:"step3_b" },
          ].map(step => (
            <div key={step.n}>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:64,fontWeight:700,color:"var(--bg3)",lineHeight:1,marginBottom:16,letterSpacing:"-0.04em"}}>{step.n}</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,fontWeight:700,marginBottom:12,lineHeight:1.2}}>{t(step.hk,lang)}</div>
              <p style={{fontSize:14,color:"var(--ink3)",lineHeight:1.8,margin:0}}>{t(step.bk,lang)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Trusted Sellers ─────────────────────────────────────────────── */}
      {verifiedSellers.length > 0 && (
        <div style={{marginBottom:48}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:20}}>
            <div>
              <span className="sl">Trusted Network</span>
              <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:28,fontWeight:700,margin:0}}>People who know their watches.</h2>
            </div>
            <button className="btn-ghost" style={{fontSize:12,padding:"8px 18px",flexShrink:0}} onClick={()=>setView("market")}>All sellers →</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:14}}>
            {verifiedSellers.slice(0, 3).map(s => <SellerCard key={s.id} seller={s}/>)}
          </div>
        </div>
      )}

    </div>
  );
}

// ─── Watch Card ───────────────────────────────────────────────────────────────
